import Text from "@tiptap/extension-text";
import { Mark, Node as PMNode } from "@tiptap/pm/model";
import { Plugin, PluginKey } from "@tiptap/pm/state";

// Content saved from this editor is compiled as a mustache template, so the
// text inside {{ }} must reach the server exactly as the user typed it. Two
// things in the markdown round-trip break that:
//   1. A mark over part of a tag (e.g. "on.to" pasted as a link from Outlook
//      or Teams) serializes as markdown syntax inside the tag.
//   2. Every text node is markdown-escaped (_ * [ ] ` ~ \) and HTML-escaped
//      (< >), turning {{a_b}} next to a mark into {{a\_b}}.
// Either one makes the template fail to compile.

// One tag at a time: a tag can't contain a brace, so the match can never run
// from the end of one tag to the start of the next. Newlines are excluded
// because non-text inline nodes (hard breaks) are mapped to "\n" below.
const MUSTACHE_TAG = /\{\{\{[^{}\n]*\}\}\}|\{\{[^{}\n]*\}\}/g;

export const findMustacheTags = (text: string): Array<[number, number]> =>
  Array.from(text.matchAll(MUSTACHE_TAG), (m) => [
    m.index!,
    m.index! + m[0].length,
  ]);

// Marks that cover only part of a tag within `block` (which starts at
// `blockPos`), as ranges to remove. A mark wrapping the whole tag is kept —
// **{{name}}** is still a valid template.
const partialTagMarks = (block: PMNode, blockPos: number) => {
  let text = "";
  const marksAt: (readonly Mark[])[] = [];
  block.forEach((child) => {
    // Every inline node is size 1 per character (text) or 1 (leaf), so string
    // index i is document position blockPos + 1 + i.
    const chunk = child.isText ? child.text! : "\n";
    text += chunk;
    for (let i = 0; i < chunk.length; i++) marksAt.push(child.marks);
  });

  const removals: Array<{ from: number; to: number; mark: Mark }> = [];
  findMustacheTags(text).forEach(([start, end]) => {
    const seen = new Set<Mark>();
    for (let i = start; i < end; i++) {
      marksAt[i].forEach((mark) => {
        if (seen.has(mark)) return;
        seen.add(mark);
        const coversTag = marksAt
          .slice(start, end)
          .every((marks) => mark.isInSet(marks));
        if (!coversTag) {
          removals.push({
            from: blockPos + 1 + start,
            to: blockPos + 1 + end,
            mark,
          });
        }
      });
    }
  });
  return removals;
};

const mustacheTagMarksKey = new PluginKey("mustacheTagMarks");

// Replaces StarterKit's Text so tags are kept literal: partial marks are
// stripped from tags after every doc change (typing, paste, setContent), which
// leaves each tag inside a single text node; the serializer then writes tag
// text verbatim and escapes everything else as before.
export const MustacheText = Text.extend({
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: mustacheTagMarksKey,
        appendTransaction(transactions, _oldState, newState) {
          if (!transactions.some((tr) => tr.docChanged)) return null;

          const tr = newState.tr;
          newState.doc.descendants((node, pos) => {
            if (!node.isTextblock) return true;
            if (node.textContent.includes("{{")) {
              partialTagMarks(node, pos).forEach(({ from, to, mark }) =>
                tr.removeMark(from, to, mark)
              );
            }
            return false;
          });
          return tr.docChanged ? tr : null;
        },
      }),
    ];
  },

  addStorage() {
    return {
      markdown: {
        serialize(state: any, node: PMNode) {
          const text = node.text!;
          let last = 0;
          findMustacheTags(text).forEach(([start, end]) => {
            if (start > last) state.text(escapeHTML(text.slice(last, start)));
            state.text(text.slice(start, end), false);
            last = end;
          });
          if (last < text.length) state.text(escapeHTML(text.slice(last)));
        },
        parse: {},
      },
    };
  },
});

// Same as tiptap-markdown's own text serializer applies outside tags.
const escapeHTML = (value: string) =>
  value.replace(/</g, "&lt;").replace(/>/g, "&gt;");
